/**
 * Fixed Asset API Client
 * Epic 66: Tárgyi Eszköz Nyilvántartás & ÉCS
 */

import { api } from './client';

// ============================================
// TYPES
// ============================================

export interface FixedAsset {
  id: string;
  tenantId: string;
  name: string;
  inventoryNumber?: string;
  category: 'MACHINERY' | 'IT_EQUIPMENT' | 'VEHICLE';
  status: 'ACTIVE' | 'FULLY_DEPRECIATED' | 'SCRAPPED' | 'SOLD';
  purchasePrice: number;
  currentValue: number;
  depreciationRate: number;
  commissionDate: string;
  productId?: string;
  warehouseId?: string;
  serialNumber?: string;
  note?: string;
  scrappedAt?: string;
  createdBy: string;
  createdAt: string;
  updatedAt: string;
}

export interface FixedAssetQueryResult {
  assets: FixedAsset[];
  total: number;
  offset: number;
  limit: number;
}

export interface DepreciationReportItem {
  asset: FixedAsset;
  periodDepreciation: number;
  cumulativeDepreciation: number;
  remainingValue: number;
}

export interface DepreciationReport {
  tenantId: string;
  periodLabel: string;
  items: DepreciationReportItem[];
  totalPurchaseValue: number;
  totalCurrentValue: number;
  totalPeriodDepreciation: number;
}

// ============================================
// API CALLS
// ============================================

export async function createFixedAsset(data: {
  name: string;
  category: string;
  purchasePrice: number;
  commissionDate: string;
  inventoryNumber?: string;
  productId?: string;
  warehouseId?: string;
  serialNumber?: string;
  note?: string;
}) {
  return api.post<{ data: FixedAsset }>('/fixed-assets', data);
}

export async function listFixedAssets(params?: {
  category?: string;
  status?: string;
  offset?: number;
  limit?: number;
}) {
  const query = new URLSearchParams();
  if (params?.category !== undefined) query.set('category', params.category);
  if (params?.status !== undefined) query.set('status', params.status);
  if (params?.offset !== undefined) query.set('offset', String(params.offset));
  if (params?.limit !== undefined) query.set('limit', String(params.limit));
  const qs = query.toString();
  return api.get<{ data: FixedAssetQueryResult }>(`/fixed-assets${qs ? `?${qs}` : ''}`);
}

export async function getFixedAsset(id: string) {
  return api.get<{ data: FixedAsset }>(`/fixed-assets/${id}`);
}

export async function getFullyDepreciatedAssets() {
  return api.get<{ data: FixedAsset[] }>('/fixed-assets/fully-depreciated');
}

export async function getDepreciationReport(params: {
  year: number;
  month?: number;
  quarter?: number;
  category?: string;
}) {
  const query = new URLSearchParams();
  query.set('year', String(params.year));
  if (params.month !== undefined) query.set('month', String(params.month));
  if (params.quarter !== undefined) query.set('quarter', String(params.quarter));
  if (params.category !== undefined) query.set('category', params.category);
  return api.get<{ data: DepreciationReport }>(
    `/fixed-assets/depreciation/report?${query.toString()}`
  );
}

export async function runDepreciation(data: { year: number; month: number }) {
  return api.post<{ data: { processed: number; fullyDepreciated: number } }>(
    '/fixed-assets/depreciation/run',
    data
  );
}
